import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'
import { useLucideIcon, FALLBACK_ICON } from './lucideRegistry'

/**
 * ReactiveJSON primitive — renders a Lucide icon by name.
 *
 * YAML usage:
 *   - type: LucideIcon
 *     name: ImageIcon       # any export from lucide-react
 *     size: 12              # optional, default 16
 *     strokeWidth: 1.5      # optional
 *     color: "white"        # optional
 *     attributes:
 *       class: "shrink-0"
 *       aria-hidden: true
 *
 * Or with dynamic mapping (e.g. align value → icon name):
 *   - type: LucideIcon
 *     name: ~~.titleAlign
 *     map: {left: "AlignLeft", center: "AlignCenter", right: "AlignRight"}
 */
export const LucideIcon = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const rawName = resolve(props.name)
  const map = props.map
  const name = map && Object.prototype.hasOwnProperty.call(map, rawName) ? map[rawName] : rawName
  const size = resolve(props.size) ?? 16
  const strokeWidth = resolve(props.strokeWidth)
  const color = resolve(props.color)
  const attributes = useEvaluatedAttributes(props.attributes ?? {})

  const iconProps = { size, ...attributes }
  if (strokeWidth !== undefined) iconProps.strokeWidth = strokeWidth
  if (color !== undefined) iconProps.color = color

  // Registry icons resolve synchronously; any other name lazy-loads from the
  // full Lucide set (so the whole library is no longer in the main bundle).
  const Icon = useLucideIcon(name)

  let rendered
  if (Icon === undefined) {
    // Lazy icon still loading — reserve space to avoid a layout shift.
    const dim = typeof size === 'number' ? `${size}px` : size
    rendered = (
      <span
        aria-hidden="true"
        className={attributes.className}
        style={{ display: 'inline-block', width: dim, height: dim }}
      />
    )
  } else {
    const Resolved = Icon || FALLBACK_ICON
    rendered = <Resolved {...iconProps} />
  }

  return <ActionDependant {...props}>{rendered}</ActionDependant>
}
